import { Box, Checkbox, Menu, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import useAPI from "../../../../../api/hooks/useAPI";
import useColors from "../../../../../api/hooks/useColors";
import { entityKeys, labelKeys } from "../../../../../api/queryKeys";
import TextButton from "../../../../../components/buttons/TextButton";
import LabelChip from "../../../../../components/LabelChip";
import LoadingIcon from "../../../../../components/LoadingIcon";
import { Toast } from "../../../../../components/Toast";
import { EnvFeatureFlagContext } from "../../../../../contexts/EnvFeaturesContext";
import SvgToIcon from "../../../../../icons/SvgToIcon";
import { LABEL_COLORS } from "../../../../../utils/constants";
import { icons } from "../../icons/iconExport";
import { RDS_ENTITIES_QUERY_KEY_PREFIX } from "../constants";
import {
  computeChangedIds,
  createOptimisticMutationHandlers,
} from "../entityMutationUtils";
import DeleteLabelMenu from "./DeleteLabelMenu";
import LabelsDropdownHeader from "./LabelsDropdownHeader";

const labelCheckboxState = {
  CHECKED: "checked",
  UNCHECKED: "unchecked",
  INDETERMINATE: "indeterminate",
};

const findInitialCheckboxStates = (labels, selectedEntities) => {
  const checkboxStates = {};
  for (const label of labels) {
    const allEntitiesHaveLabel = selectedEntities.every((entity) =>
      entity.labels?.includes(label.label_id)
    );
    const someEntitiesHaveLabel = selectedEntities.some((entity) =>
      entity.labels?.includes(label.label_id)
    );
    if (allEntitiesHaveLabel) {
      checkboxStates[label.name] = labelCheckboxState.CHECKED;
    } else if (someEntitiesHaveLabel) {
      checkboxStates[label.name] = labelCheckboxState.INDETERMINATE;
    } else {
      checkboxStates[label.name] = labelCheckboxState.UNCHECKED;
    }
  }
  return checkboxStates;
};

const LabelsDropdown = ({
  selectedEntities,
  menuAnchorEl,
  setMenuAnchorEl,
}) => {
  const colors = useColors();
  const { trackEvent } = useAPI();
  const queryClient = useQueryClient();
  const { isActiveRds } = useContext(EnvFeatureFlagContext);
  const initialCheckboxStates = useRef({});
  const [inputValue, setInputValue] = useState("");
  const [checkboxStates, setCheckboxStates] = useState({});
  const [hoveredLabel, setHoveredLabel] = useState(null);
  const {
    createLabel,
    getAllLabels,
    updateEntitiesLabelsBatch,
    refreshEntitiesOverview,
    deleteLabel,
  } = useAPI();

  const labelsQuery = useQuery({
    queryKey: labelKeys.all,
    queryFn: getAllLabels,
    select: (data) => data.data,
  });

  const allLabels = useMemo(
    () => labelsQuery.data?.filter((label) => !label.is_deleted) || [],
    [labelsQuery.data]
  );
  const allLabelNames = allLabels.map((label) => label.name);

  const nameToIdMap = allLabels.reduce((mapping, label) => {
    mapping[label.name] = label.label_id;
    return mapping;
  }, {});

  useEffect(() => {
    if (
      labelsQuery.isSuccess &&
      Object.keys(initialCheckboxStates.current).length === 0 &&
      allLabels.length > 0
    ) {
      initialCheckboxStates.current = findInitialCheckboxStates(
        allLabels,
        selectedEntities
      );
      setCheckboxStates(initialCheckboxStates.current);
      trackEvent("User opened Labels Dropdown");
    }
  }, [labelsQuery.isSuccess, allLabels, selectedEntities, trackEvent]);

  const indeterminateEligibleLabels = Object.entries(
    initialCheckboxStates.current
  )
    .filter(
      ([_, checkboxState]) => checkboxState === labelCheckboxState.INDETERMINATE
    )
    .map(([labelID]) => labelID);

  const anyChangesInCheckboxStates = Object.entries(checkboxStates).some(
    ([labelID, state]) => state !== initialCheckboxStates.current[labelID]
  );

  const toggleCheckBox = (label) => {
    const newCheckboxStates = { ...checkboxStates };

    switch (checkboxStates[label.name]) {
      case labelCheckboxState.CHECKED:
        newCheckboxStates[label.name] = indeterminateEligibleLabels.includes(
          label.name
        )
          ? labelCheckboxState.INDETERMINATE
          : labelCheckboxState.UNCHECKED;
        break;
      case labelCheckboxState.INDETERMINATE:
        newCheckboxStates[label.name] = labelCheckboxState.UNCHECKED;
        break;
      case labelCheckboxState.UNCHECKED:
        newCheckboxStates[label.name] = labelCheckboxState.CHECKED;
        break;
      default:
        break;
    }

    setCheckboxStates(newCheckboxStates);
    trackEvent("User toggled a label checkbox");
  };

  const onMenuClose = () => {
    setMenuAnchorEl(null);
    setInputValue("");
  };

  const updateLabels = async ({ labelIdsToAdd, labelIdsToRemove }) => {
    const entityIDsToUpdate = [];
    for (const entity of selectedEntities) {
      if (
        labelIdsToAdd.every((labelId) => entity.labels?.includes(labelId)) &&
        labelIdsToRemove.every((labelId) => !entity.labels?.includes(labelId))
      )
        continue;
      else entityIDsToUpdate.push(entity.entity_id);
    }

    if (entityIDsToUpdate.length > 0) {
      await updateEntitiesLabelsBatch(
        entityIDsToUpdate,
        labelIdsToAdd,
        labelIdsToRemove
      );
    }

    if (isActiveRds) {
      await refreshEntitiesOverview();
      queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey?.[0] === "string" &&
          query.queryKey[0].startsWith(RDS_ENTITIES_QUERY_KEY_PREFIX),
      });
      queryClient.invalidateQueries({
        queryKey: entityKeys.entitiesOverview(),
      });
      return;
    }
  };

  const optimisticHandlers = createOptimisticMutationHandlers({
    queryClient,
    selectedEntities,
    fieldName: "labels",
    successMessage: "Labels updated successfully",
    errorMessage: "Error updating entity labels",
  });

  const updateLabelsMutation = useMutation({
    mutationFn: updateLabels,
    onMutate: ({ labelIdsToAdd, labelIdsToRemove }) =>
      optimisticHandlers.onMutate({
        idsToAdd: labelIdsToAdd,
        idsToRemove: labelIdsToRemove,
      }),
    onSuccess: () => Toast.success(optimisticHandlers.successMessage),
    onError: (error, variables, context) => {
      optimisticHandlers.onError(error, variables, context);
      Toast.error(optimisticHandlers.errorMessage);
    },
    onSettled: optimisticHandlers.onSettled,
  });

  const deleteLabelMutation = useMutation({
    mutationFn: async (labelId) => {
      await deleteLabel(labelId);
      await queryClient.refetchQueries({ queryKey: labelKeys.all });
    },
    onSuccess: (data, labelId) => {
      Toast.success("Label deleted successfully");
      const labelToDelete = allLabels.find(
        (label) => label.label_id === labelId
      );
      if (labelToDelete) {
        const newCheckboxStates = { ...checkboxStates };
        delete newCheckboxStates[labelToDelete.name];
        setCheckboxStates(newCheckboxStates);
      }
    },
    onError: () => {
      Toast.error("Error deleting label");
    },
  });

  const filteredLabels = allLabels
    ? allLabels.filter((label) =>
        label?.name?.toLowerCase().startsWith(inputValue.toLowerCase())
      )
    : [];

  // choose a color for a new label; the color should be different from the existing labels, and after all the colors are used, the color should be selected randomly from the list of colors
  const getColorForNewLabel = () => {
    const usedColors = new Set(allLabels.map((label) => label.color));
    // get the colors that are not used yet
    const availableColors = Object.values(LABEL_COLORS).filter(
      (color) => !usedColors.has(color)
    );
    // if there are no available colors, select a random color from the list of colors
    if (availableColors.length === 0) {
      return Object.values(LABEL_COLORS)[
        Math.floor(Math.random() * Object.values(LABEL_COLORS).length)
      ];
    } else {
      return availableColors[
        Math.floor(Math.random() * availableColors.length)
      ];
    }
  };

  const handleCreateLabel = async () => {
    const newLabel = inputValue.trim();
    if (newLabel && !allLabelNames.includes(newLabel)) {
      const newLabelColor = getColorForNewLabel();
      await createLabel(newLabel, newLabelColor);
      queryClient.invalidateQueries({ queryKey: labelKeys.all });

      if (selectedEntities && selectedEntities.length) {
        const newCheckboxStates = { ...checkboxStates };
        newCheckboxStates[newLabel] = labelCheckboxState.CHECKED;
        setCheckboxStates(newCheckboxStates);
      }
      setInputValue("");
    }
  };

  const showCreateButton = inputValue && !filteredLabels.length;

  /** Applies label changes by computing diff and triggering optimistic mutation. */
  const handleApplyChanges = () => {
    trackEvent("User clicked Apply changes button in Labels Dropdown");

    const { idsToAdd: labelNamesToAdd, idsToRemove: labelNamesToRemove } =
      computeChangedIds(
        checkboxStates,
        initialCheckboxStates.current,
        labelCheckboxState.CHECKED,
        labelCheckboxState.UNCHECKED
      );

    const labelIdsToAdd = labelNamesToAdd
      .map((name) => nameToIdMap[name])
      .filter(Boolean);
    const labelIdsToRemove = labelNamesToRemove
      .map((name) => nameToIdMap[name])
      .filter(Boolean);

    onMenuClose();
    updateLabelsMutation.mutate({ labelIdsToAdd, labelIdsToRemove });
  };

  return (
    <Menu
      id="simple-menu"
      anchorEl={menuAnchorEl}
      open={Boolean(menuAnchorEl)}
      onClose={onMenuClose}
      sx={{
        ".MuiPaper-root": {
          borderRadius: "9px",
          border: "1px solid " + colors.grey_200,
          boxShadow: "0px 1px 4px 0px " + colors.grey_500,
          display: "flex",
          padding: "4px 8px",
          flexDirection: "column",
        },
        "& .MuiMenu-list": {
          padding: "0",
        },
      }}
    >
      {labelsQuery.isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="80px"
        >
          <LoadingIcon size={18} />
        </Box>
      ) : labelsQuery.isSuccess ? (
        <Box display="flex" flexDirection="column">
          <LabelsDropdownHeader
            inputValue={inputValue}
            setInputValue={setInputValue}
            onCreateLabel={handleCreateLabel}
            showCreateButton={showCreateButton}
          />
          <Box
            sx={{
              display: "flex",
              flexWrap: "nowrap",
              minWidth: "210px",
              width: "min-content",
              overflowY: "auto",
              overflowX: "hidden",
              flexDirection: "column",
              maxHeight: "300px",
              "&::-webkit-scrollbar": { width: 4 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: colors.grey_400,
                borderRadius: 4,
              },
            }}
          >
            {filteredLabels.length > 0 && (
              <Box
                height="28px"
                display="flex"
                alignItems="center"
                marginTop="4px"
                paddingX="6px"
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.text_secondary,
                    fontWeight: 500,
                    lineHeight: "16px",
                    marginTop: "6px",
                    marginBottom: "6px",
                  }}
                >
                  Labels
                </Typography>
              </Box>
            )}
            {filteredLabels.map((label) => (
              <Box
                key={label.name}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={0.5}
                width="100%"
                onMouseEnter={() => setHoveredLabel(label.label_id)}
                onMouseLeave={() => setHoveredLabel(null)}
                sx={{
                  padding: "2px 4px",
                  "&:hover": {
                    backgroundColor: colors.grey_4percent,
                    borderRadius: "6px",
                  },
                }}
              >
                <Box display="flex" alignItems="center" flex={1}>
                  <Checkbox
                    checked={
                      checkboxStates[label.name] === labelCheckboxState.CHECKED
                    }
                    indeterminate={
                      checkboxStates[label.name] ===
                      labelCheckboxState.INDETERMINATE
                    }
                    icon={
                      <SvgToIcon
                        IconLogo={icons.Unchecked}
                        style={{ width: "16px", height: "16px" }}
                      />
                    }
                    checkedIcon={
                      <SvgToIcon
                        IconLogo={icons.Checked}
                        style={{ width: "16px", height: "16px" }}
                      />
                    }
                    indeterminateIcon={
                      <SvgToIcon
                        IconLogo={icons.Indeterminate}
                        style={{ width: "16px", height: "16px" }}
                      />
                    }
                    onChange={() => toggleCheckBox(label)}
                    disabled={selectedEntities?.length === 0}
                    sx={{ padding: "8px" }}
                  />
                  <LabelChip
                    text={label.name}
                    size="M"
                    type="noBorder"
                    color={label.color}
                    textColor={colors.text_primary}
                    sx={{ paddingX: "4px" }}
                  />
                </Box>
                {hoveredLabel === label.label_id && (
                  <DeleteLabelMenu
                    label={label}
                    onDeleteLabel={(labelId) =>
                      deleteLabelMutation.mutate(labelId)
                    }
                    deleteLabelMutation={deleteLabelMutation}
                  />
                )}
              </Box>
            ))}
          </Box>
          <Box display="flex" flexDirection="column">
            <TextButton
              onClick={handleApplyChanges}
              color="primary"
              size="small"
              disabled={
                !anyChangesInCheckboxStates || updateLabelsMutation.isPending
              }
              loading={updateLabelsMutation.isPending}
              sx={{
                width: "100%",
                justifyContent: "flex-start",
                paddingX: "4px",
              }}
            >
              <Box paddingLeft={0.5}>
                <Typography variant="caption">Apply changes</Typography>
              </Box>
            </TextButton>
          </Box>
        </Box>
      ) : null}
    </Menu>
  );
};

export default LabelsDropdown;
